import React, { useState, ChangeEvent, useRef } from 'react';
import ReferenceDropdown from '../components/ReferenceDropdown';
import axios from 'axios';
import ReferenceMultiSelect from './ReferenceMultiSelect';

const FORM_STEPS = [
  { id: 'overview',    title: 'Case Overview' },
  { id: 'patient',     title: 'Patient Profile' },
  { id: 'operations',  title: 'Operations' },
  { id: 'documents',   title: 'Documents' },
];

// --- INTERFACES ---
interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ClientData {
  salutation: string;
  name: string;
  relationship: string;
  contact_number: string;
  secondary_contact_number: string;
  email: string;
}

interface TreatingDetail {
  doctor: string;
  department: string;
  hospital: string;
}

interface FileWithId {
  id: string;
  file: File;
}

interface DutyDetail {
  duty_type: string;
  duty_type_specify: string;
  duty_rate: string;
}

interface PatientData {
  status: 'Active' | 'Suspended' | 'Stopped';
  case_start_date: string;
  registration_date: string;
  duty_details: DutyDetail[];
  case_manager: string;
  referred_by: string[];
  billing_pattern: string;
  customer_category: string;
  patient_salutation: string;
  patient_name: string;
  age: string;
  sex: string;
  citizenship: string;
  blood_group: string;
  height: string;
  weight: string;
  address: string;
  location_link: string;
  hospital: string;
  treating_details: TreatingDetail[];
  medical_history_list: string[];
  present_status_list: string[];
  general_information: string;
  languages: string[];
  religion: string;
  clients_info: ClientData[];
  photo: FileWithId[];
  discharge_files: FileWithId[];
  reg_files: FileWithId[];
  id_proof_files: FileWithId[];
}

interface MultiFileUploaderProps {
  files: FileWithId[];
  onChange: (files: FileWithId[]) => void;
  label?: string;
  accept?: string;
}

// --- CONSTANTS ---
const DUTY_TYPES = ['08 Hrs', '12 Dual', '12 Day', '12 Night', '24 Hrs', 'Nursing Visit', 'Physiotherapy', 'Medical Equipment', 'Consumables'];
const BLOOD_GROUPS = ['A +ve', 'A -ve', 'B +ve', 'B -ve', 'AB +ve', 'AB -ve', 'O +ve', 'O -ve'];

// --- STYLES ---
const styles: { [key: string]: React.CSSProperties } = {
  overlay:            { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modalWrapper:       { backgroundColor: 'white', borderRadius: '12px', width: '98%', maxWidth: '1600px', height: '96vh', display: 'flex', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', position: 'relative' },
  sidebar:            { width: '260px', backgroundColor: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarHeader:      { padding: '1.25rem', borderBottom: '1px solid #e2e8f0', backgroundColor: '#fff' },
  sidebarContent:     { padding: '1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  stepTab:            { padding: '12px 14px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', border: '1px solid transparent' },
  activeTab:          { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' },
  stepTitle:          { fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' },
  activeText:         { color: '#1d4ed8' },
  mainArea:           { flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#fff', overflow: 'hidden' },
  scrollArea:         { flex: 1, overflowY: 'auto', padding: '1.5rem 2.5rem' },
  sectionBlock:       { marginBottom: '2rem' },
  sectionTitle:       { fontSize: '1rem', fontWeight: 700, color: '#0f172a', margin: '0 0 1rem 0', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' },
  rowFlex:            { display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap', width: '100%', marginBottom: '1rem' },
  formGroup:          { display: 'flex', flexDirection: 'column', position: 'relative', gap: '0.3rem', flex: 1, minWidth: '110px' },
  formGroupAuto:      { display: 'flex', flexDirection: 'column', position: 'relative', gap: '0.3rem', flex: '0 0 auto' },
  label:              { fontSize: '0.75rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:              { padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', outline: 'none', width: '100%', boxSizing: 'border-box', height: '40px' },
  textArea:           { padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', outline: 'none', minHeight: '80px', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%', resize: 'vertical' },
  footer:             { padding: '1rem 2.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' },
  button:             { padding: '0.6rem 1.2rem', fontSize: '0.875rem', fontWeight: 600, borderRadius: '6px', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s' },
  cancelButton:       { backgroundColor: '#e2e8f0', color: '#475569' },
  saveButton:         { backgroundColor: '#3b82f6', color: 'white' },
  smallBtn:           { padding: '0 0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #cbd5e1', cursor: 'pointer', backgroundColor: '#fff', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  addBtn:             { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '0 1rem', cursor: 'pointer', borderRadius: '4px', fontWeight: 600, height: '40px', display: 'flex', alignItems: 'center', flexShrink: 0 },
  removeBtn:          { backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' },
  card:               { backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '6px', marginBottom: '0.75rem', border: '1px solid #e2e8f0' },
  cardHeader:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: 600, color: '#64748b' },
  resetBtn:           { padding: '0 0.6rem', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer', color: '#64748b', height: '40px' },
  uploadBox:          { border: '1px dashed #94a3b8', borderRadius: '6px', padding: '0 0.5rem', textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px' },
  fileList:           { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', marginTop: '2px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
  fileItem:           { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0.5rem', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' },
  progressOverlay:    { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
  progressBarContainer: { width: '60%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' },
  progressBarFill:    { height: '100%', backgroundColor: '#3b82f6', transition: 'width 0.3s ease' },
  hint:               { fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px' },
};

// --- MultiFileUploader ---
const MultiFileUploader: React.FC<MultiFileUploaderProps> = ({ files, onChange, label, accept }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles: FileWithId[] = Array.from(e.target.files).map(file => ({
        id: Math.random().toString(36).substr(2, 9), file
      }));
      onChange([...files, ...newFiles]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeFile = (id: string) => onChange(files.filter(f => f.id !== id));
  return (
    <div style={{ ...styles.formGroup, minWidth: '160px' }}>
      {label && <label style={styles.label}>{label}</label>}
      <div style={styles.uploadBox} onClick={() => fileInputRef.current?.click()}>
        <input type="file" multiple accept={accept} ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
        <span style={{ color: '#475569', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {files.length > 0 ? `${files.length} file(s)` : '📄 Upload Docs'}
        </span>
      </div>
      {files.length > 0 && (
        <div style={styles.fileList}>
          {files.map((item) => (
            <div key={item.id} style={styles.fileItem}>
              <span style={{ fontWeight: 500, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80%' }}>{item.file.name}</span>
              <span onClick={(e) => { e.stopPropagation(); removeFile(item.id); }} style={{ color: '#ef4444', cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold' }}>&times;</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
const AddPatientModal: React.FC<AddPatientModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeStepId, setActiveStepId] = useState('overview');

  const initialClient: ClientData = { salutation: '', name: '', relationship: '', contact_number: '', secondary_contact_number: '', email: '' };
  const initialTreatingDetail: TreatingDetail = { doctor: '', department: '', hospital: '' };

  const initialPatientState: PatientData = {
    status: 'Active',
    case_start_date: '',
    registration_date: new Date().toISOString().substring(0, 10),
    duty_details: [{ duty_type: '08 Hrs', duty_type_specify: '', duty_rate: '' }],
    case_manager: '',
    referred_by: [''],
    billing_pattern: 'Monthly',
    customer_category: 'Middle Class',
    patient_salutation: '',
    patient_name: '',
    age: '',
    sex: '',
    citizenship: '',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    location_link: '',
    hospital: '',
    treating_details: [{ ...initialTreatingDetail }],
    medical_history_list: [''],
    present_status_list: [''],
    general_information: '',
    languages: [''],
    religion: 'Hindu',
    clients_info: [{ ...initialClient }],
    photo: [],
    discharge_files: [],
    reg_files: [],
    id_proof_files: [],
  };

  const [patientData, setPatientData] = useState<PatientData>(initialPatientState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPatientData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilesChange = (name: keyof PatientData, files: FileWithId[]) => {
    setPatientData(prev => ({ ...prev, [name]: files }));
  };

  const handleDutyChange = (index: number, field: keyof DutyDetail, value: string) => {
    const updated = [...patientData.duty_details];
    if (field === 'duty_type' && value !== 'Specify') updated[index].duty_type_specify = '';
    updated[index] = { ...updated[index], [field]: value };
    setPatientData(prev => ({ ...prev, duty_details: updated }));
  };
  const addDutyRow = () => {
    if (patientData.duty_details.length < 4)
      setPatientData(prev => ({ ...prev, duty_details: [...prev.duty_details, { duty_type: '08 Hrs', duty_type_specify: '', duty_rate: '' }] }));
  };
  const removeDutyRow = (index: number) => {
    if (patientData.duty_details.length > 1)
      setPatientData(prev => ({ ...prev, duty_details: prev.duty_details.filter((_, i) => i !== index) }));
  };

  const handleMedicalHistoryChange = (index: number, value: string) => {
    const updated = [...patientData.medical_history_list]; updated[index] = value;
    setPatientData(prev => ({ ...prev, medical_history_list: updated }));
  };
  const addMedicalHistory = () => setPatientData(prev => ({ ...prev, medical_history_list: [...prev.medical_history_list, ''] }));
  const removeMedicalHistory = (index: number) => setPatientData(prev => ({ ...prev, medical_history_list: prev.medical_history_list.filter((_, i) => i !== index) }));

  const handlePresentStatusChange = (index: number, value: string) => {
    const updated = [...patientData.present_status_list]; updated[index] = value;
    setPatientData(prev => ({ ...prev, present_status_list: updated }));
  };
  const addPresentStatus = () => setPatientData(prev => ({ ...prev, present_status_list: [...prev.present_status_list, ''] }));
  const removePresentStatus = (index: number) => setPatientData(prev => ({ ...prev, present_status_list: prev.present_status_list.filter((_, i) => i !== index) }));

  const handleClientChange = (index: number, field: keyof ClientData, value: string) => {
    const updated = [...patientData.clients_info];
    updated[index] = { ...updated[index], [field]: value };
    setPatientData(prev => ({ ...prev, clients_info: updated }));
  };
  const addClient = () => {
    if (patientData.clients_info.length < 3) setPatientData(prev => ({ ...prev, clients_info: [...prev.clients_info, { ...initialClient }] }));
    else alert('Maximum 3 clients allowed.');
  };
  const removeClient = (index: number) => setPatientData(prev => ({ ...prev, clients_info: prev.clients_info.filter((_, i) => i !== index) }));

  const handleTreatingChange = (index: number, field: keyof TreatingDetail, value: string) => {
    const updated = [...patientData.treating_details];
    updated[index] = { ...updated[index], [field]: value };
    setPatientData(prev => ({ ...prev, treating_details: updated }));
  };
  const addTreatingDetail = () => {
    if (patientData.treating_details.length < 3)
      setPatientData(prev => ({ ...prev, treating_details: [...prev.treating_details, { ...initialTreatingDetail }] }));
  };
  const removeTreatingDetail = (index: number) => {
    if (index === 0) return;
    setPatientData(prev => ({ ...prev, treating_details: prev.treating_details.filter((_, i) => i !== index) }));
  };

  const handleReferredByChange = (index: number, value: string) => {
    const updated = [...patientData.referred_by]; updated[index] = value;
    setPatientData(prev => ({ ...prev, referred_by: updated }));
  };
  const addReferredBy = () => { if (patientData.referred_by.length < 3) setPatientData(prev => ({ ...prev, referred_by: [...prev.referred_by, ''] })); };
  const removeReferredBy = (index: number) => {
    if (index === 0) return;
    setPatientData(prev => ({ ...prev, referred_by: prev.referred_by.filter((_, i) => i !== index) }));
  };

  const handleNavigation = (direction: 'next' | 'prev') => {
    const currentIndex = FORM_STEPS.findIndex(s => s.id === activeStepId);
    if (direction === 'next' && currentIndex < FORM_STEPS.length - 1) setActiveStepId(FORM_STEPS[currentIndex + 1].id);
    else if (direction === 'prev' && currentIndex > 0) setActiveStepId(FORM_STEPS[currentIndex - 1].id);
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUploadProgress(0);
    const token = localStorage.getItem('accessToken');

    const cleanValue = (val: string | undefined) => {
      if (val === undefined || val === null) return null;
      if (typeof val === 'string' && val.trim() === '') return null;
      return val;
    };

    const primaryDuty = patientData.duty_details[0] || {};
    const dataToSend = {
      ...patientData,
      duty_type: primaryDuty.duty_type || '',
      duty_type_specify: primaryDuty.duty_type_specify || '',
      photo: undefined,
      discharge_files: undefined,
      reg_files: undefined,
      id_proof_files: undefined,
      referred_by: patientData.referred_by.filter(r => r.trim() !== ''),
      medical_history_list: patientData.medical_history_list.filter(m => m.trim() !== ''),
      present_status_list: patientData.present_status_list.filter(p => p.trim() !== ''),
      languages: patientData.languages.filter(l => l.trim() !== ''),
      treating_details: patientData.treating_details
        .filter(t => t.doctor.trim() !== '' || patientData.hospital.trim() !== '')
        .map(t => ({ ...t, hospital: patientData.hospital })),
      clients_info: patientData.clients_info
        .filter(c => c.name.trim() !== '')
        .map(c => ({
          salutation: c.salutation,
          name: c.name,
          relationship: c.relationship,
          contact_number: c.contact_number,
          secondary_contact_number: c.secondary_contact_number,
          email: c.email,
        })),
      duty_details: patientData.duty_details.filter(d => d.duty_type !== ''),
      height: cleanValue(patientData.height),
      weight: cleanValue(patientData.weight),
      case_start_date: cleanValue(patientData.case_start_date),
      registration_date: cleanValue(patientData.registration_date),
    };

    const formData = new FormData();
    formData.append('patient_data', JSON.stringify(dataToSend));
    if (patientData.photo.length > 0) formData.append('photo', patientData.photo[0].file);
    patientData.discharge_files.forEach(f => formData.append('discharge_summary', f.file));
    patientData.reg_files.forEach(f => formData.append('registration_form', f.file));
    patientData.id_proof_files.forEach(f => formData.append('customer_id_proof', f.file));

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      await axios.post(`${API_BASE_URL}/api/patients/`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
      });
      alert('Patient added successfully!');
      setPatientData(initialPatientState);
      setActiveStepId('overview');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.response) alert(`Failed: ${JSON.stringify(error.response.data.detail || error.response.data)}`);
      else alert('Failed to add patient.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  const isFirstStep = FORM_STEPS.findIndex(s => s.id === activeStepId) === 0;
  const isLastStep  = FORM_STEPS.findIndex(s => s.id === activeStepId) === FORM_STEPS.length - 1;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalWrapper} onClick={e => e.stopPropagation()}>

        {isSubmitting && (
          <div style={styles.progressOverlay}>
            <div style={{ width: '60%', textAlign: 'center', backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Saving...</h3>
              <div style={styles.progressBarContainer}>
                <div style={{ ...styles.progressBarFill, width: `${uploadProgress}%` }} />
              </div>
              <p style={{ marginTop: '10px', color: '#64748b' }}>{uploadProgress}% Complete</p>
            </div>
          </div>
        )}

        {/* SIDEBAR */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>New Client Registration</h2>
          </div>
          <div style={styles.sidebarContent}>
            {FORM_STEPS.map((step) => (
              <div key={step.id} onClick={() => setActiveStepId(step.id)} style={{ ...styles.stepTab, ...(activeStepId === step.id ? styles.activeTab : {}) }}>
                <div style={{ ...styles.stepTitle, ...(activeStepId === step.id ? styles.activeText : {}) }}>{step.title}</div>
              </div>
            ))}
          </div>
        </div>

        {/* MAIN AREA */}
        <div style={styles.mainArea}>
          <div style={styles.scrollArea} ref={scrollContainerRef}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
              <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>

            {/* ===== STEP 1: CASE OVERVIEW ===== */}
            {activeStepId === 'overview' && (
              <div>
                {/* Client Information */}
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Client Information</h3>

                  {/* Primary client */}
                  <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ flexShrink: 0 }}>
                      <label style={{ ...styles.label, display: 'block', marginBottom: '0.4rem' }}>Photo</label>
                      <div
                        onClick={() => document.getElementById('patient-photo-input')?.click()}
                        style={{ width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed #94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', backgroundColor: '#f8fafc', boxSizing: 'border-box' }}
                      >
                        {patientData.photo.length > 0
                          ? <img src={URL.createObjectURL(patientData.photo[0].file)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <><span style={{ fontSize: '1.8rem', color: '#94a3b8' }}>📷</span><span style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '4px' }}>Upload</span></>
                        }
                      </div>
                      <input id="patient-photo-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleFilesChange('photo', [{ id: Math.random().toString(36).substr(2, 9), file: e.target.files[0] }]); }} />
                      {patientData.photo.length > 0 && <span onClick={() => handleFilesChange('photo', [])} style={{ fontSize: '0.75rem', color: '#ef4444', cursor: 'pointer', fontWeight: 600, display: 'block', textAlign: 'center', marginTop: '4px' }}>Remove</span>}
                    </div>

                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <div style={{ width: '110px', flexShrink: 0 }}>
                          <ReferenceDropdown label="Title" category="salutation" value={patientData.clients_info[0]?.salutation || ''} onChange={(val) => handleClientChange(0, 'salutation', val)} />
                        </div>
                        <div style={{ ...styles.formGroup, flex: 1 }}>
                          <label style={styles.label}>Client Full Name</label>
                          <input style={styles.input} value={patientData.clients_info[0]?.name || ''} onChange={(e) => handleClientChange(0, 'name', e.target.value)} placeholder="Full Name" required />
                        </div>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Relationship</label>
                        <input style={styles.input} value={patientData.clients_info[0]?.relationship || ''} onChange={(e) => handleClientChange(0, 'relationship', e.target.value)} placeholder="e.g. Son" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Primary Contact</label>
                        <input style={styles.input} value={patientData.clients_info[0]?.contact_number || ''} onChange={(e) => handleClientChange(0, 'contact_number', e.target.value)} placeholder="+91 00000 00000" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Secondary Contact</label>
                        <input style={styles.input} value={patientData.clients_info[0]?.secondary_contact_number || ''} onChange={(e) => handleClientChange(0, 'secondary_contact_number', e.target.value)} placeholder="+91 00000 00000" />
                      </div>
                      <div style={{ ...styles.formGroup, gridColumn: 'span 2' }}>
                        <label style={styles.label}>Email</label>
                        <input style={styles.input} type="email" value={patientData.clients_info[0]?.email || ''} onChange={(e) => handleClientChange(0, 'email', e.target.value)} placeholder="client@example.com" />
                      </div>
                    </div>
                  </div>

                  {/* Additional clients */}
                  {patientData.clients_info.slice(1).map((client, i) => {
                    const idx = i + 1;
                    return (
                      <div key={idx} style={{ ...styles.card, marginBottom: '0.5rem' }}>
                        <div style={styles.cardHeader}>
                          <span>CLIENT #{idx + 1}</span>
                          <button type="button" onClick={() => removeClient(idx)} style={{ ...styles.smallBtn, ...styles.removeBtn, fontSize: '0.8rem', padding: '0 0.6rem' }}>Remove</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                            <div style={{ width: '110px', flexShrink: 0 }}>
                              <ReferenceDropdown label="Title" category="salutation" value={client.salutation} onChange={(val) => handleClientChange(idx, 'salutation', val)} />
                            </div>
                            <div style={{ ...styles.formGroup, flex: 1 }}>
                              <label style={styles.label}>Client Name</label>
                              <input style={styles.input} value={client.name} onChange={(e) => handleClientChange(idx, 'name', e.target.value)} placeholder="Full Name" />
                            </div>
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Relationship</label>
                            <input style={styles.input} value={client.relationship} onChange={(e) => handleClientChange(idx, 'relationship', e.target.value)} />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Primary Contact</label>
                            <input style={styles.input} value={client.contact_number} onChange={(e) => handleClientChange(idx, 'contact_number', e.target.value)} />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Secondary Contact</label>
                            <input style={styles.input} value={client.secondary_contact_number} onChange={(e) => handleClientChange(idx, 'secondary_contact_number', e.target.value)} />
                          </div>
                          <div style={{ ...styles.formGroup, gridColumn: 'span 2' }}>
                            <label style={styles.label}>Email</label>
                            <input style={styles.input} type="email" value={client.email} onChange={(e) => handleClientChange(idx, 'email', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {patientData.clients_info.length < 3 && (
                    <button type="button" onClick={addClient} style={styles.addBtn}>+ Add Another Client</button>
                  )}
                </div>

                {/* Patient Information */}
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Patient Information</h3>
                  <div style={styles.rowFlex}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flex: 2, minWidth: '180px' }}>
                      <div style={{ width: '110px', flexShrink: 0 }}>
                        <ReferenceDropdown label="Title" category="salutation" value={patientData.patient_salutation} onChange={(val) => setPatientData(prev => ({ ...prev, patient_salutation: val }))} />
                      </div>
                      <div style={{ ...styles.formGroup, flex: 1 }}>
                        <label style={styles.label}>Patient Full Name</label>
                        <input style={styles.input} name="patient_name" value={patientData.patient_name} onChange={handleChange} placeholder="Full Name" required />
                      </div>
                    </div>
                    <div style={{ ...styles.formGroup, flex: 0.5, minWidth: '80px' }}>
                      <label style={styles.label}>Age</label>
                      <input style={styles.input} name="age" value={patientData.age} onChange={handleChange} placeholder="e.g. 65" />
                    </div>
                    <div style={{ ...styles.formGroup, flex: 0.6, minWidth: '90px' }}>
                      <label style={styles.label}>Sex</label>
                      <select style={styles.input} name="sex" value={patientData.sex} onChange={handleChange}>
                        <option value="">-</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Treating Details */}
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Treating Details</h3>
                  {patientData.treating_details.map((detail, idx) => (
                    <div key={idx} style={{ ...styles.rowFlex, backgroundColor: '#f8fafc', padding: '0.5rem', borderRadius: '6px', border: '1px solid #f1f5f9', marginBottom: '0.5rem' }}>
                      <div style={{ ...styles.formGroup, flex: 2 }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                          <div style={{ flex: 1 }}>
                            <ReferenceDropdown label="Doctor Name" category="doctor_name" value={detail.doctor} onChange={(val) => handleTreatingChange(idx, 'doctor', val)} />
                          </div>
                          {idx !== 0 && (
                            <button type="button" onClick={() => removeTreatingDetail(idx)} style={{ ...styles.smallBtn, ...styles.removeBtn, width: '40px', padding: 0 }}>&times;</button>
                          )}
                          {idx === patientData.treating_details.length - 1 && patientData.treating_details.length < 3 && (
                            <button type="button" onClick={addTreatingDetail} style={styles.addBtn}>+ Add Doctor</button>
                          )}
                        </div>
                        <span style={styles.hint}>e.g. Dr. Yash Bhushan - Orthopedic</span>
                      </div>
                      {idx === 0 ? (
                        <div style={{ ...styles.formGroup, flex: 2 }}>
                          <ReferenceDropdown label="Hospital" category="hospital" value={patientData.hospital} onChange={(val) => setPatientData(prev => ({ ...prev, hospital: val }))} />
                          <span style={styles.hint}>e.g. Apollo Speciality Hospital</span>
                        </div>
                      ) : (
                        <div style={{ flex: 2 }} />
                      )}
                    </div>
                  ))}
                </div>

                {/* Location */}
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Location</h3>
                  <div style={styles.rowFlex}>
                    <div style={{ ...styles.formGroup, flex: 2, minWidth: '200px' }}>
                      <label style={styles.label}>Residential Address</label>
                      <textarea style={styles.textArea} name="address" value={patientData.address} onChange={handleChange} placeholder="Full residential address" />
                    </div>
                    <div style={{ ...styles.formGroup, flex: 1.5, minWidth: '180px' }}>
                      <label style={styles.label}>Google Maps Link</label>
                      <input style={styles.input} name="location_link" value={patientData.location_link} onChange={handleChange} placeholder="https://maps.google.com/..." />
                    </div>
                  </div>
                </div>

                {/* Registration */}
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Registration</h3>
                  <div style={styles.rowFlex}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Registration Date</label>
                      <input type="date" style={styles.input} name="registration_date" value={patientData.registration_date} onChange={handleChange} required />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Case Start Date</label>
                      <input type="date" style={styles.input} name="case_start_date" value={patientData.case_start_date} onChange={handleChange} />
                    </div>
                    {patientData.referred_by.map((ref, idx) => (
                      <div key={idx} style={styles.formGroup}>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-end' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <ReferenceDropdown
                              label={idx === 0 ? 'Referred By' : `Referred By #${idx + 1}`}
                              category="referred_by"
                              value={ref}
                              onChange={(val) => handleReferredByChange(idx, val)}
                            />
                          </div>
                          {idx > 0 && <button type="button" onClick={() => removeReferredBy(idx)} style={{ ...styles.smallBtn, ...styles.removeBtn }}>&times;</button>}
                        </div>
                      </div>
                    ))}
                    {patientData.referred_by.length < 3 && (
                      <div style={styles.formGroupAuto}>
                        <label style={{ ...styles.label, visibility: 'hidden' }}>add</label>
                        <button type="button" onClick={addReferredBy} style={styles.addBtn}>+ Add Ref</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ===== STEP 2: PATIENT PROFILE ===== */}
            {activeStepId === 'patient' && (
              <div>
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Additional Patient Details</h3>
                  <div style={styles.rowFlex}>
                    <div style={styles.formGroup}>
                      <ReferenceDropdown
                        label="Citizenship"
                        category="citizenship"
                        value={patientData.citizenship}
                        onChange={(val) => setPatientData(prev => ({ ...prev, citizenship: val }))}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Blood Group</label>
                      <select style={styles.input} name="blood_group" value={patientData.blood_group} onChange={handleChange}>
                        <option value="">-</option>
                        {BLOOD_GROUPS.map(bg => <option key={bg} value={bg}>{bg}</option>)}
                      </select>
                    </div>
                    <div style={{ ...styles.formGroup, flex: 0.5, minWidth: '80px' }}>
                      <label style={styles.label}>Height</label>
                      <input style={styles.input} name="height" value={patientData.height} onChange={handleChange} placeholder="cm" />
                    </div>
                    <div style={{ ...styles.formGroup, flex: 0.5, minWidth: '80px' }}>
                      <label style={styles.label}>Weight</label>
                      <input style={styles.input} name="weight" value={patientData.weight} onChange={handleChange} placeholder="kg" />
                    </div>
                  </div>
                </div>

                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Languages</h3>
                  <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                    <ReferenceMultiSelect label="Patient Languages" category="languages" selectedValues={patientData.languages} onChange={(newLangs) => setPatientData(prev => ({ ...prev, languages: newLangs }))} />
                  </div>
                </div>

                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Medical Condition</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                    {patientData.medical_history_list.map((item, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexGrow: 1, maxWidth: '450px' }}>
                        <div style={{ flex: 1 }}>
                          <ReferenceDropdown label={`Condition #${index + 1}`} category="medical_history" value={item} onChange={(val) => handleMedicalHistoryChange(index, val)} />
                        </div>
                        {index > 0 && <button type="button" onClick={() => removeMedicalHistory(index)} style={{ ...styles.smallBtn, ...styles.removeBtn }}>✕</button>}
                      </div>
                    ))}
                    <button type="button" onClick={addMedicalHistory} style={styles.addBtn}>+ Add Condition</button>
                  </div>
                </div>

                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Present Medical Status</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                    {patientData.present_status_list.map((item, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexGrow: 1, maxWidth: '450px' }}>
                        <div style={{ flex: 1 }}>
                          <ReferenceDropdown label={`Status #${index + 1}`} category="present_status" value={item} onChange={(val) => handlePresentStatusChange(index, val)} />
                        </div>
                        {index > 0 && <button type="button" onClick={() => removePresentStatus(index)} style={{ ...styles.smallBtn, ...styles.removeBtn }}>✕</button>}
                      </div>
                    ))}
                    <button type="button" onClick={addPresentStatus} style={styles.addBtn}>+ Add Status</button>
                  </div>
                </div>

                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>General Information</h3>
                  <textarea style={styles.textArea} name="general_information" value={patientData.general_information} onChange={handleChange} placeholder="Enter any general notes or information." />
                </div>
              </div>
            )}

            {/* ===== STEP 3: OPERATIONS ===== */}
            {activeStepId === 'operations' && (
              <div>
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Rate & Duty Type</h3>
                  <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f9fafb' }}>
                    {patientData.duty_details.map((detail, index) => (
                      <div key={index} style={{ marginBottom: index < patientData.duty_details.length - 1 ? '1rem' : 0, paddingBottom: index < patientData.duty_details.length - 1 ? '1rem' : 0, borderBottom: index < patientData.duty_details.length - 1 ? '1px dashed #cbd5e1' : 'none' }}>
                        <div style={styles.rowFlex}>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Type of Duty {index + 1}</label>
                            {detail.duty_type === 'Specify' ? (
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <input style={styles.input} placeholder="Specify Type" value={detail.duty_type_specify} onChange={(e) => handleDutyChange(index, 'duty_type_specify', e.target.value)} autoFocus />
                                <button type="button" style={styles.resetBtn} onClick={() => handleDutyChange(index, 'duty_type', '08 Hrs')}>x</button>
                              </div>
                            ) : (
                              <select style={styles.input} value={detail.duty_type} onChange={(e) => handleDutyChange(index, 'duty_type', e.target.value)}>
                                <option value="">Select...</option>
                                {DUTY_TYPES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                <option value="Specify" style={{ fontWeight: 'bold', color: '#3b82f6' }}>Specify...</option>
                              </select>
                            )}
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Rate (Cost)</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input style={{ ...styles.input, flex: 1 }} value={detail.duty_rate} onChange={(e) => handleDutyChange(index, 'duty_rate', e.target.value)} placeholder="e.g. 1500" type="number" />
                              {patientData.duty_details.length > 1 && (
                                <button type="button" style={{ ...styles.smallBtn, ...styles.removeBtn }} onClick={() => removeDutyRow(index)}>X</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {patientData.duty_details.length < 4 && (
                      <button type="button" onClick={addDutyRow} style={{ ...styles.addBtn, marginTop: '0.5rem' }}>+ Add Duty</button>
                    )}
                  </div>
                </div>

                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Billing & Category</h3>
                  <div style={styles.rowFlex}>
                    <div style={styles.formGroup}>
                      <ReferenceDropdown label="Billing Pattern" category="billing_pattern" value={patientData.billing_pattern} onChange={(val) => setPatientData(prev => ({ ...prev, billing_pattern: val }))} />
                    </div>
                    <div style={styles.formGroup}>
                      <ReferenceDropdown label="Customer Category" category="customer_category" value={patientData.customer_category} onChange={(val) => setPatientData(prev => ({ ...prev, customer_category: val }))} />
                    </div>
                  </div>
                </div>

                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Case Management</h3>
                  <div style={styles.rowFlex}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Case Status</label>
                      <select style={styles.input} name="status" value={patientData.status} onChange={handleChange}>
                        <option value="Active">Active</option>
                        <option value="Suspended">Suspended</option>
                        <option value="Stopped">Stopped</option>
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <ReferenceDropdown label="Case Manager" category="case_manager" value={patientData.case_manager} onChange={(val) => setPatientData(prev => ({ ...prev, case_manager: val }))} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== STEP 4: DOCUMENTS ===== */}
            {activeStepId === 'documents' && (
              <div>
                <div style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>Documents</h3>
                  <div style={styles.rowFlex}>
                    <MultiFileUploader label="Discharge Summary" files={patientData.discharge_files} onChange={(files) => handleFilesChange('discharge_files', files)} />
                    <MultiFileUploader label="Registration Form" files={patientData.reg_files} onChange={(files) => handleFilesChange('reg_files', files)} />
                    <MultiFileUploader label="Customer ID Proof" files={patientData.id_proof_files} onChange={(files) => handleFilesChange('id_proof_files', files)} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div style={styles.footer}>
            <div>
              {!isFirstStep && (
                <button type="button" onClick={() => handleNavigation('prev')} style={{ ...styles.button, ...styles.cancelButton }}>Back</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" onClick={onClose} style={{ ...styles.button, ...styles.cancelButton }}>Cancel</button>
              {!isLastStep ? (
                <button type="button" onClick={() => handleNavigation('next')} style={{ ...styles.button, ...styles.saveButton }}>Next Step</button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{ ...styles.button, backgroundColor: '#059669', color: 'white', opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                >
                  {isSubmitting ? 'Saving...' : 'Save Registration'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPatientModal;
